import {Inject, injectable} from 'inversify-props';
import Jwt from '@/model/Jwt';

import {ILogger} from '@/utils/ILogger';
import {LogdownLogger} from '@/utils/LogdownLogger';

import {IApiService} from '@/services/IApi.service';
import {IAuthService} from '@/services/IAuth.service';

export class AuthenticationError extends Error {

  public errorCode: number;

  constructor(errorCode: number, message: string) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    this.errorCode = errorCode;
  }
}

@injectable()
export class AuthService implements IAuthService {

  private logger: ILogger = new LogdownLogger('AuthService');

  @Inject(Symbol.for('IApiService'))
  private apiService!: IApiService;

  private readonly path: string = '/auth';

  public async login(username: string, password: string): Promise<Jwt> {

    const authorizationHeader = Buffer.from(`${username}:${password}`, 'utf8').toString('base64');

    this.logger.trace(`Logging in...`, username);

    try {
      const loginResponse = await this.apiService.get(`${this.path}/login`, {
        // We are not using the axios 'auth' config because it doesn't encode the correct way (it uses ASCII)
        headers: {
          authorization: `Basic ${authorizationHeader}`,
        },
      });

      this.logger.info(`User '${username}' logged in`, loginResponse);
      return new Jwt(loginResponse.token);

    } catch (error) {
      this.logger.error(`Could not log in with user '${username}'`, error);
      throw new AuthenticationError(error.response.status, error.response.data.detail);
    }
  }

  public async refresh(): Promise<Jwt> {
    this.logger.info(`Refreshing authentication token`);

    try {
      const refreshResponse = await this.apiService.get(`${this.path}/refresh`, null);

      this.logger.info(`Authentication token refreshed`);

      return new Jwt(refreshResponse.token);
    } catch (error) {
      this.logger.error(`Could not refresh authentication token`, error);
      throw new AuthenticationError(error.response.status, error.response.data.detail);
    }
  }
}
